// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { GetSeacoastQuote } from "@gcg/App/Services/RequestQuoteService";
import { screen } from "@gcg/XDL/Helpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import OrderDetailsSummaryTableV2 from "@seacoastusa/App/Components/CustomeCartLineDetails/OrderDetailsSummaryTableV2";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import { CustomOrderLineModel } from "@seacoastusa/App/Components/CustomeCartLineDetails/CustomCartLineDetails";

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const parsedQuery = parseQueryString<{ cartId?: string }>(search);
    const cartId = parsedQuery.cartId;
    return {
        cartId,
    };
};

const mapDispatchToProps = {
    clearCurrentCart,
};

interface OrderLineModel {
    orderLines: Array<CustomOrderLineModel>;
}

interface QuoteLineModel {
    address1: string;
    address2: string;
    city: string;
    companyName: string;
    firstName: string;
    country: string;
    currency: string;
    email: string;
    lastName: string;
    notes: string;
    phone: string;
    postalCode: string;
    state: string;
    orderLineModel: OrderLineModel;
}

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface CustomCartConfirmationStyles {
    detailsGridContainer?: GridContainerProps;
    dataGridItem: GridItemProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerGridContainer?: GridContainerProps;
    jobNameGridItem?: GridItemProps;
    jobNameHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    dateGridItem?: GridItemProps;
    dateLabelText?: TypographyPresentationProps;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToGridItem?: GridItemProps;
    shipToHeadingAndText?: SmallHeadingAndTextStyles;
    rightColumnGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    commonSmallHeadingText?: SmallHeadingAndTextStyles;
    emailPhoneLinks?: LinkPresentationProps;
    wrapper?: InjectableCss;
    titleText?: TypographyProps;
    contentText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spacer?: InjectableCss;
    formControlWrapperGridItem?: GridItemProps;
    formControlWrapperFullGridItem?: GridItemProps;
    requestQuoteFormWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    pageWrapper?: InjectableCss;
}

export const CustomCartConfirmationStyles: CustomCartConfirmationStyles = {
    wrapper: {
        css: css`
            margin-bottom: 5px;
            width: 100%;
        `,
    },
    titleText: {
        variant: "h6",
        as: "h2",
        weight: "bolder",
        css: css`
            margin-bottom: 0;
            padding-right: 6px;
            white-space: nowrap;
        `,
    },
    contentText: {
        css: css`
            line-height: 20px;
        `,
    },
    dataGridItem: {},
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 300px;
        `,
    },
    spacer: {
        css: css`
            height: 30px;
        `,
    },
    formControlWrapperGridItem: {
        width: [12, 6, 3, 3, 3],
    },
    formControlWrapperFullGridItem: {
        width: 12,
    },
    requestQuoteFormWrapper: {
        css: css`
            position: relative;
            width: 67%;
            ${screen("lte", "md")`
                 width:100%
            `}
        `,
    },
    titleWrapper: {
        css: css`
            width: 30%;
            display: flex;
            justify-content: space-between;
            border-bottom: 1px solid ${getColor("text.main")};
            padding-bottom: 15px;

            ${screen("lte", "md")`
                 width:100%
            `}

            & h3 {
                font-size: 26px;
                line-height: 32px;
                font-weight: bold;
            }

            & span {
                display: block;
                font-size: 14px;
                line-height: 32px;
            }
        `,
    },
    pageWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-direction: row;
            justify-content: space-between;
            ${screen("lte", "md")`
                 flex-direction: column;
                 width:100%
            `}
        `,
    },
};

const styles = CustomCartConfirmationStyles;

const Separator = styled.hr`
    width: 100%;
    margin: 20px 0;
`;

const CustomCartConfirmationDisplay: FC<Props> = ({ cartId, clearCurrentCart }) => {
    const [quote, setQuote] = useState<QuoteLineModel>({} as QuoteLineModel);
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        setIsLoading(true);
        GetSeacoastQuote(cartId)
            .then(response => {
                setQuote(response);
                clearCurrentCart();
                setIsLoading(false);
            })
            .catch(error => {
                setIsLoading(false);
            });
    }, []);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner color="primary.main" />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.pageWrapper}>
            <StyledWrapper {...styles.requestQuoteFormWrapper}>
                <GridContainer>
                    {quote.firstName && (
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("First Name")}</Typography>
                                <Typography {...styles.contentText}>{quote.firstName}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {quote.lastName && (
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("Last Name")}</Typography>
                                <Typography {...styles.contentText}>{quote.lastName}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {quote.email && (
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("Email Address")}</Typography>
                                <Typography {...styles.contentText}>{quote.email}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {quote.phone && (
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("Phone")}</Typography>
                                <Typography {...styles.contentText}>{quote.phone}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}

                    {quote.companyName && (
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("Company Name")}</Typography>
                                <Typography {...styles.contentText}>{quote.companyName}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {quote.country && (
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("Country")}</Typography>
                                <Typography {...styles.contentText}>{quote.country}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {quote.currency && (
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("Currency")}</Typography>
                                <Typography {...styles.contentText}>{quote.currency}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                </GridContainer>
                <GridItem {...styles.formControlWrapperFullGridItem}>
                    <Separator />
                </GridItem>
                <GridContainer>
                    {quote.address1 && (
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("Address1")}</Typography>
                                <Typography {...styles.contentText}>{quote.address1}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {quote.address2 && (
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("Address2")}</Typography>
                                <Typography {...styles.contentText}>{quote.address2}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {quote.city && (
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("Town/City")}</Typography>
                                <Typography {...styles.contentText}>{quote.city}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {quote.state && (
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("State")}</Typography>
                                <Typography {...styles.contentText}>{quote.state}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                    {quote.postalCode && (
                        <GridItem {...styles.formControlWrapperGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("ZIP / Postal Code")}</Typography>
                                <Typography {...styles.contentText}>{quote.postalCode}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                </GridContainer>
                <GridItem {...styles.formControlWrapperFullGridItem}>
                    <Separator />
                </GridItem>
                <GridContainer>
                    {quote.notes && (
                        <GridItem {...styles.formControlWrapperFullGridItem}>
                            <StyledWrapper {...styles.wrapper}>
                                <Typography {...styles.titleText}>{translate("Comments")}</Typography>
                                <Typography {...styles.contentText}>{quote.notes}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    )}
                </GridContainer>
                {quote.notes ? (
                    <GridItem {...styles.formControlWrapperFullGridItem}>
                        <Separator />
                    </GridItem>
                ) : (
                    <StyledWrapper {...styles.spacer} />
                )}
                <OrderDetailsSummaryTableV2 orderLines={quote?.orderLineModel?.orderLines || []} />
            </StyledWrapper>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography as="h3">{translate("Quote Summary")}</Typography>
                <StyledWrapper
                    css={css`
                        display: inline-flex;
                        line-height: 32px;
                    `}
                >
                    <Typography as="span">
                        {quote?.orderLineModel?.orderLines?.length} {translate("Items")}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomCartConfirmationDisplay),
    definition: {
        displayName: "CustomCartConfirmationDisplay",
        allowedContexts: ["CustomCartConfirmationPage"],
        group: "Cart",
    },
};

export default widgetModule;
